import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { EventTimePickerComponent } from '@app/v2/event/components/event-dates-and-timings/components/event-time-picker/event-time-picker.component'
import { TimeRangePickerComponent } from '@app/v2/event/components/event-dates-and-timings/components/time-range-picker/time-range-picker.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { SavingIndicatorComponent } from '@app/v2/shared/components/saving-indicator/saving-indicator.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { EventTimingStatus } from '../../enums/event.enum'
import { EventService } from '../../services/event.service'
import { DayWeekShort, Event, EventDay, EventSlot, EventTimings, EventTimingsResponse, UpdateEventTimingsPayload } from '../../types/event.type'
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component'
import { EventSchedulerComponent } from './components/event-scheduler/event-scheduler.component'
import { snapMinuteToEventPickerGrid } from './components/event-time-picker/event-time-picker.component'
import { EventTimingExceptionsComponent } from './components/event-timing-exceptions/event-timing-exceptions.component'
import { WeeklyHoursEditorComponent } from './components/weekly-hours-editor/weekly-hours-editor.component'
import {
  END_OF_DAY_EDIT_SLOT_FROM,
  END_OF_DAY_EDIT_SLOT_TO,
  MIN_SLOT_DURATION_H,
  buildUpdateDayPayload,
  clampMinuteToken,
  coerceIndefiniteWeeklyRange,
  eventTimingsRowsToDays,
  finiteTimingsHydrationKey,
  firstTimingSlot,
  formatPublicDateTimeForTimingsPayload,
  formatTimezoneLabel,
  indefiniteOpeningHoursModeFromWeekRows,
  mergeTimeSlots,
  normalizeApiTimingDay,
  normalizeDayWeekShort,
  padHourPart,
  parsePublicDateTimeFromApi,
  parsePublicRangeFromApi,
  parseTimeToHours,
  perDayHoursFromFirstSlot,
  sortSlotsByTime
} from './utils/event-dates-timings.utils'

/**
 * @description Milliseconds to show the success alert after timings-related saves.
 */
const ALERT_SUCCESS_TIMINGS_UPDATED_MS: number = 5000

/**
 * @description Default main time range applied when the selected finite span forces default hours.
 */
const DEFAULT_MAIN_TIME_RANGE: { startTime: string; endTime: string } = { startTime: '10:00', endTime: '22:00' }

@Component({
  selector: 'app-event-dates-and-timings',
  standalone: true,
  templateUrl: './event-dates-and-timings.component.html',
  styleUrl: './event-dates-and-timings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    EventSchedulerComponent,
    EventTimingExceptionsComponent,
    DateRangePickerComponent,
    TimeRangePickerComponent,
    EventTimePickerComponent,
    WeeklyHoursEditorComponent,
    ModalComponent,
    DeleteModalComponent,
    SavingIndicatorComponent
  ]
})
export class EventDatesAndTimingsComponent {
  public readonly event = input<Event | null>(null)
  public readonly finish = output<void>()
  private readonly _eventService: EventService = inject(EventService)
  private readonly _alertService: AlertService = inject(AlertService)
  private readonly _destroyRef: DestroyRef = inject(DestroyRef)
  private readonly _modalService: ModalService = inject(ModalService)

  public readonly isSavingTimings = signal<boolean>(false)
  public readonly showTimingsSavedIndicator = signal<boolean>(false)
  public readonly isResettingTimingExceptions = signal<boolean>(false)
  public readonly timingExceptionsResetVersion = signal<number>(0)

  public readonly timezoneLabel = computed<string>(() => {
    const ev: Event | null = this.event()
    const tzFromCheckin: string | undefined | null = ev?.checkins?.[0]?.location_details?.timezone
    return formatTimezoneLabel(tzFromCheckin || ev?.timezone)
  })

  public readonly days = signal<EventDay[]>([])
  public readonly dateRange = signal<{ startDate: string; endDate: string | null } | null>(null)
  public readonly timeRange = signal<{ startTime: string; endTime: string } | null>(null)
  public readonly isSavingDay = signal<string | null>(null)
  /** When set, scheduler shows add-slot skeleton for this merged range and hides the matching slot bar. */
  public readonly slotAddSavePreview = signal<{ dayId: string; overlayFrom: string; overlayTo: string } | null>(null)
  public readonly isIndefinite = signal<boolean>(false)

  public readonly apiResetStartDate = signal<string | null>(null)
  public readonly apiResetEndDate = signal<string | null>(null)

  public readonly weekDayOptions: readonly DayWeekShort[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  public readonly dayWeekFullName: Record<DayWeekShort, string> = {
    Sun: 'Sunday',
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday'
  }

  public readonly selectedWeekDays = signal<DayWeekShort[]>([])
  public readonly openingHoursMode = signal<'same' | 'different'>('same')
  public readonly sameHoursStartHour = signal<string>('10')
  public readonly sameHoursStartMinute = signal<string>('00')
  public readonly sameHoursEndHour = signal<string>('22')
  public readonly sameHoursEndMinute = signal<string>('00')
  public readonly perDayHours = signal<Record<DayWeekShort, { startHour: string; startMinute: string; endHour: string; endMinute: string }>>({
    Sun: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Mon: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Tue: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Wed: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Thu: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Fri: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' },
    Sat: { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' }
  })

  public readonly indefiniteApiByDay = signal<Record<DayWeekShort, { timingId: string | null; info: string; label: string }>>({
    Sun: { timingId: null, info: '', label: '' },
    Mon: { timingId: null, info: '', label: '' },
    Tue: { timingId: null, info: '', label: '' },
    Wed: { timingId: null, info: '', label: '' },
    Thu: { timingId: null, info: '', label: '' },
    Fri: { timingId: null, info: '', label: '' },
    Sat: { timingId: null, info: '', label: '' }
  })

  public readonly isSavingIndefinite = signal<boolean>(false)

  public readonly indefiniteSameHoursRangeError = computed<string | null>(() => {
    if (!this.isIndefinite()) return null
    if (this.selectedWeekDays().length === 0) return null
    if (this.openingHoursMode() !== 'same') return null
    const sh: string = this.sameHoursStartHour()
    const sm: string = this.sameHoursStartMinute()
    const eh: string = this.sameHoursEndHour()
    const em: string = this.sameHoursEndMinute()
    if (parseTimeToHours(`${eh}:${em}`) > parseTimeToHours(`${sh}:${sm}`)) return null
    return `Closing time (${eh}:${em}) must be after opening time (${sh}:${sm}).`
  })

  public readonly perDayErrorByDay = computed<Record<DayWeekShort, string | null>>(() => {
    const out: Record<DayWeekShort, string | null> = {
      Sun: null,
      Mon: null,
      Tue: null,
      Wed: null,
      Thu: null,
      Fri: null,
      Sat: null
    }
    if (!this.isIndefinite()) return out
    if (this.openingHoursMode() !== 'different') return out
    const selected: DayWeekShort[] = [...this.selectedWeekDays()]
    if (!selected.length) return out
    for (const d of selected) {
      out[d] = this.indefiniteDayHoursRangeError(d)
    }
    return out
  })

  private _indefiniteBulkTimer: ReturnType<typeof setTimeout> | null = null
  private _finiteWeeklyBulkTimer: ReturnType<typeof setTimeout> | null = null

  public readonly pendingDateRange = signal<{ startDate: string; endDate: string | null } | null>(null)
  public readonly pendingTimeRange = signal<{ startTime: string; endTime: string } | null>(null)

  public readonly confirmMainRangeModalId: string = 'event-main-range-confirm-modal'
  public readonly confirmMainRangeModalMessage: string =
    'You are about to change the main date and time range for your event, this will reflect on all the edits you have made. Are you sure you want to proceed?'

  public readonly editDayHoursModalId: string = 'event-edit-day-hours-modal'
  public readonly editDayHoursModalHeader: string = 'Edit day hours'
  public readonly editDayHoursHeaderIcon: string = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.4516 0.621206L11.1745 1.97018L14.1146 5.07562L15.391 3.72665C15.584 3.52276 15.7372 3.2807 15.8417 3.01428C15.9462 2.74786 16 2.4623 16 2.17393C16 1.88555 15.9462 1.6 15.8417 1.33358C15.7372 1.06716 15.584 0.825093 15.391 0.621206C14.9948 0.222442 14.4685 0 13.9213 0C13.374 0 12.8478 0.222442 12.4516 0.621206Z" fill="#111928"/><path d="M12.9389 6.31744L9.99872 3.212L6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744Z" fill="#111928"/><path d="M6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744L9.99872 3.212L6.06991 7.35815Z" fill="#111928"/><path d="M9.80581 11.4894C9.57348 11.7344 9.27787 11.9015 8.95602 11.9698L6.01586 12.5907C5.74746 12.6473 5.46997 12.6331 5.20802 12.5493C4.94607 12.4655 4.70776 12.3147 4.51422 12.1103C4.32068 11.9058 4.17791 11.6541 4.09856 11.3774C4.01921 11.1008 4.00574 10.8077 4.05935 10.5242L4.64722 7.41876C4.7119 7.07881 4.87012 6.76659 5.10205 6.5212L9.193 2.20028C9.17025 2.1184 9.15849 2.03355 9.15808 1.94822H1.80185C1.3241 1.94869 0.866052 2.14935 0.528235 2.50615C0.190419 2.86296 0.000440318 3.34676 0 3.85136V14.0969C0.000440318 14.6015 0.190419 15.0853 0.528235 15.4421C0.866052 15.7989 1.3241 15.9995 1.80185 16H11.5021C11.9798 15.9995 12.4379 15.7989 12.7757 15.4421C13.1135 15.0853 13.3035 14.6015 13.3039 14.0969V7.79464L9.80581 11.4894Z" fill="#111928"/></svg>`

  public readonly editModalHourOptions: string[] = Array.from({ length: 24 }, (_, i: number) => String(i).padStart(2, '0'))
  public readonly editModalMinuteOptions: readonly string[] = ['00', '15', '30', '45', '59']

  public readonly editModalStartHour = signal<string>('00')
  public readonly editModalStartMinute = signal<string>('00')
  public readonly editModalEndHour = signal<string>('23')
  public readonly editModalEndMinute = signal<string>('59')

  public readonly editModalStartTime = signal<string>('00:00')
  public readonly editModalEndTime = signal<string>('23:59')
  public readonly editModalScope = signal<'day' | 'entire'>('day')
  public readonly editModalDayId = signal<string | null>(null)
  public readonly editModalSlotIndex = signal<number | null>(null)
  public readonly editModalSnapshot = signal<{ start: string; end: string; scope: 'day' | 'entire' } | null>(null)
  public readonly isSavingEditModal = signal<boolean>(false)

  public readonly editModalTimeOrderError = computed<string | null>(() => {
    const startStr: string = `${this.editModalStartHour()}:${this.editModalStartMinute()}`
    const endStr: string = `${this.editModalEndHour()}:${this.editModalEndMinute()}`
    if (parseTimeToHours(startStr) < parseTimeToHours(endStr)) return null
    return 'Opening time must be before closing time.'
  })

  public readonly addDaySlotModalId: string = 'event-add-day-slot-modal'
  public readonly addDaySlotModalHeader: string = 'Add time slot'
  public readonly addDaySlotHeaderIcon: string = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 3.75V14.25M3.75 9H14.25" stroke="#111928" stroke-width="1.5" stroke-linecap="round"/></svg>`
  public readonly addModalDayId = signal<string | null>(null)
  public readonly addModalStartHour = signal<string>('00')
  public readonly addModalStartMinute = signal<string>('00')
  public readonly addModalEndHour = signal<string>('01')
  public readonly addModalEndMinute = signal<string>('00')

  public readonly organizationId = computed<number | undefined>(() => this.event()?.workspace_id)
  public readonly eventId = computed<number | undefined>(() => this.event()?.id)

  public readonly showWeeklyHoursEditor = computed<boolean>(() => {
    if (this.isIndefinite()) return true
    const dr = this.dateRange()
    if (!dr?.startDate || !dr?.endDate) return false
    const start = new Date(dr.startDate + 'T12:00:00')
    const end = new Date(dr.endDate + 'T12:00:00')
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
    const oneMonthAfterStart = new Date(start)
    oneMonthAfterStart.setMonth(oneMonthAfterStart.getMonth() + 1)
    return end >= oneMonthAfterStart
  })

  public readonly showScheduler = computed<boolean>(() => {
    const dr = this.dateRange()
    const tr = this.timeRange()
    return Boolean(!this.isIndefinite() && !this.showWeeklyHoursEditor() && dr?.startDate && tr)
  })

  public readonly schedulerStartTime = computed<string>(() => this.timeRange()?.startTime ?? '00:00')
  public readonly schedulerEndTime = computed<string>(() => this.timeRange()?.endTime ?? '23:59')

  /**
   * @description True when the current selection is a finite date range of 30 days or less (enables short-range scheduler behavior).
   * @return boolean
   */
  public readonly isFiniteRange30OrLess = computed<boolean>(() => {
    if (this.isIndefinite()) return false
    const dr = this.dateRange()
    if (!dr?.startDate || !dr.endDate) return false
    const n = this.getInclusiveDaysBetween(dr.startDate, dr.endDate)
    return n > 0 && n <= 30
  })

  private _timingsApiInflight: number = 0

  private _mainRangeOverrideEventId: number | null = null
  private _mainRangeOverrideKey: string | null = null

  private _lastFiniteTimingsHydrationKey: string | null = null
  private _finiteHydrationEventId: number | null = null

  private _modeOverrideEventId: number | null = null
  private _modeOverrideIsIndefinite: boolean | null = null
  private _exceptionsResetPending: boolean = false

  /**
   * @description Maps a calendar `YYYY-MM-DD` string to a `DayWeekShort` using local noon to avoid DST edge shifts.
   * @param ymd Date string `YYYY-MM-DD`.
   * @return DayWeekShort | null
   */
  private _dayWeekShortFromYmd(ymd: string): DayWeekShort | null {
    const d = new Date((ymd ?? '').slice(0, 10) + 'T12:00:00')
    if (Number.isNaN(d.getTime())) return null
    return (['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] ?? null) as DayWeekShort | null
  }

  public readonly exceptionAllowedWeekDays = computed<DayWeekShort[] | null>(() => {
    if (this.isIndefinite()) {
      const sel = this.selectedWeekDays()
      return sel.length ? sel : null
    }
    const list = this.days()
    if (!list.length) return null
    const allowed = new Set<DayWeekShort>()
    for (const day of list) {
      if (day.state === EventTimingStatus.INACTIVE) continue
      const dayWeek = this._dayWeekShortFromYmd(day.start_date)
      if (dayWeek) allowed.add(dayWeek)
    }
    return allowed.size ? Array.from(allowed) : null
  })

  public constructor() {
    effect(() => {
      const ev: Event | null = this.event()
      const savingDayId: string | null = this.isSavingDay()
      if (!ev) {
        this._lastFiniteTimingsHydrationKey = null
        this._finiteHydrationEventId = null
        this._modeOverrideEventId = null
        this._modeOverrideIsIndefinite = null
        return
      }

      const rows = ev.timings
      const parentIsIndefinitely: boolean = Boolean(ev.is_indefinitely)
      const publicStart = ev.public_start_date
      const publicEnd = ev.public_end_date
      const eventTimezone = ev.timezone

      queueMicrotask(() => {
        if (this._finiteHydrationEventId !== ev.id) {
          const switched = this._finiteHydrationEventId !== null && this._finiteHydrationEventId !== ev.id
          this._finiteHydrationEventId = ev.id
          if (switched) {
            this._lastFiniteTimingsHydrationKey = null
            this._modeOverrideEventId = null
            this._modeOverrideIsIndefinite = null
          }
        }

        const effectiveIsIndefinite = this._modeOverrideEventId === ev.id && this._modeOverrideIsIndefinite !== null ? this._modeOverrideIsIndefinite : parentIsIndefinitely
        this.isIndefinite.set(effectiveIsIndefinite)

        if (rows != null && !effectiveIsIndefinite) {
          const key = finiteTimingsHydrationKey(ev.id, rows)
          if (this._lastFiniteTimingsHydrationKey !== key && !savingDayId) {
            if (this._lastFiniteTimingsHydrationKey === null) {
              this._lastFiniteTimingsHydrationKey = key
              this.days.set(eventTimingsRowsToDays(rows))
            }
          }
        } else {
          this._lastFiniteTimingsHydrationKey = null
        }

        const fromPublic = parsePublicRangeFromApi(publicStart, publicEnd)
        if (savingDayId || this._timingsApiInflight > 0) {
          return
        }

        if (fromPublic) {
          const parentKey = this._computeMainRangeKey(
            { startDate: fromPublic.startDate, endDate: fromPublic.endDate || null },
            { startTime: fromPublic.startTime, endTime: fromPublic.endTime }
          )
          if (this._mainRangeOverrideEventId === ev.id && this._mainRangeOverrideKey && parentKey !== this._mainRangeOverrideKey) {
            return
          }
          if (this._mainRangeOverrideEventId === ev.id && this._mainRangeOverrideKey && parentKey === this._mainRangeOverrideKey) {
            this._mainRangeOverrideEventId = null
            this._mainRangeOverrideKey = null
          }
          this.dateRange.set({ startDate: fromPublic.startDate, endDate: fromPublic.endDate })
          this.timeRange.set({
            startTime: fromPublic.startTime.slice(0, 5),
            endTime: fromPublic.endTime.slice(0, 5)
          })
          this.apiResetStartDate.set(fromPublic.startDate)
          this.apiResetEndDate.set(fromPublic.endDate)
        } else if (rows?.length) {
          const first = rows[0]
          const last = rows[rows.length - 1]
          const end = last.end_date ?? null
          this.dateRange.set({ startDate: first.start_date, endDate: end })
          this.apiResetStartDate.set(first.start_date)
          this.apiResetEndDate.set(end)
          const firstSlot = first.slots?.[0]
          const lastSlot = last.slots?.[last.slots.length - 1]
          if (firstSlot && lastSlot) {
            this.timeRange.set({
              startTime: firstSlot.from.slice(0, 5),
              endTime: lastSlot.to.slice(0, 5)
            })
          }
        }

        if (!this.isIndefinite()) {
          this._syncFiniteSelectedWeekDaysFromDays()
          this._hydrateFiniteWeeklyUiFromDays()
        }

        if (this.isIndefinite()) {
          this._prefillIndefiniteWeeklyFromApi(rows ?? [], publicStart, eventTimezone)
          this.timeRange.set(null)
          this.days.set([])
        }
      })
    })
  }

  /**
   * @description Hide "saved" indicator after animation completes.
   */
  public onTimingsSavedIndicatorDone(): void {
    this.showTimingsSavedIndicator.set(false)
  }

  /**
   * @description Weekday range error string for one weekday in different-hours mode.
   * @param day DayWeekShort weekday key
   * @return string | null
   */
  public indefiniteDayHoursRangeError(day: DayWeekShort): string | null {
    if (!this.isIndefinite() || this.openingHoursMode() !== 'different') return null
    if (!this.selectedWeekDays().includes(day)) return null
    const t = this.perDayHours()[day]
    if (parseTimeToHours(`${t.endHour}:${t.endMinute}`) > parseTimeToHours(`${t.startHour}:${t.startMinute}`)) return null
    return `Closing time (${t.endHour}:${t.endMinute}) must be after opening time (${t.startHour}:${t.startMinute}).`
  }

  /**
   * @description Toggle weekday selection for weekly hours editor.
   * @param day DayWeekShort weekday key
   */
  public toggleWeekDay(day: DayWeekShort): void {
    const cur: DayWeekShort[] = this.selectedWeekDays()
    const next: DayWeekShort[] = cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]
    const ordered: DayWeekShort[] = this.weekDayOptions.filter((d) => next.includes(d))
    this.selectedWeekDays.set(ordered)
    if (this.isIndefinite()) {
      this._scheduleIndefiniteBulkSave(false)
      return
    }
    if (this.showWeeklyHoursEditor()) {
      const shouldBeOpen: boolean = ordered.includes(day)
      this._applyFiniteWeekdayStatus(day, shouldBeOpen ? EventTimingStatus.ACTIVE : EventTimingStatus.INACTIVE)
    }
  }

  /**
   * @description Whether weekly opening-hours controls should be disabled (no weekdays selected).
   * @return boolean
   */
  public get isOpeningHoursDisabled(): boolean {
    return this.selectedWeekDays().length === 0
  }

  /**
   * @description Switch weekly opening-hours mode.
   * @param mode 'same' | 'different'
   */
  public setOpeningHoursMode(mode: 'same' | 'different'): void {
    const previousMode: 'same' | 'different' = this.openingHoursMode()
    this.openingHoursMode.set(mode)
    if (mode === 'same') {
      if (this.isIndefinite() && previousMode === 'different') {
        this.sameHoursStartHour.set('10')
        this.sameHoursStartMinute.set('00')
        this.sameHoursEndHour.set('22')
        this.sameHoursEndMinute.set('00')
        const map = { ...this.perDayHours() }
        for (const d of this.weekDayOptions) {
          map[d] = { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' }
        }
        this.perDayHours.set(map)
        this._scheduleIndefiniteBulkSave(true)
        return
      }

      if (!this.isIndefinite() && this.showWeeklyHoursEditor() && previousMode === 'different') {
        const dr = this.dateRange()
        if (dr?.startDate) {
          this._ensureFiniteDefaultMainTimeRange()
          const tr = this.timeRange()
          const startHm = (tr?.startTime ?? '10:00').slice(0, 5)
          const endHm = (tr?.endTime ?? '22:00').slice(0, 5)
          const [sh, sm] = startHm.split(':')
          const [eh, em] = endHm.split(':')
          this.sameHoursStartHour.set(padHourPart(sh ?? '10'))
          this.sameHoursStartMinute.set(clampMinuteToken(sm ?? '00'))
          this.sameHoursEndHour.set(padHourPart(eh ?? '22'))
          this.sameHoursEndMinute.set(clampMinuteToken(em ?? '00'))
        }
      }

      const startHour: string = this.sameHoursStartHour()
      const startMinute: string = this.sameHoursStartMinute()
      const endHour: string = this.sameHoursEndHour()
      const endMinute: string = this.sameHoursEndMinute()
      const map = { ...this.perDayHours() }
      for (const d of this.weekDayOptions) {
        map[d] = { startHour, startMinute, endHour, endMinute }
      }
      this.perDayHours.set(map)

      if (!this.isIndefinite() && this.showWeeklyHoursEditor() && this.selectedWeekDays().length > 0) {
        this._scheduleFiniteWeeklyBulkSave({ validateTimes: true })
      }
    }
  }

  /**
   * @description Apply same-hours changes and schedule persistence.
   */
  public onSameHoursChange(): void {
    this.setOpeningHoursMode('same')
    if (this.isIndefinite()) {
      this._scheduleIndefiniteBulkSave(true)
      return
    }
    if (this.showWeeklyHoursEditor()) {
      this._scheduleFiniteWeeklyBulkSave({ validateTimes: true })
    }
  }

  /**
   * @description Update per-day hours and schedule persistence.
   * @param day DayWeekShort weekday key
   * @param patch Partial<{ startHour: string; startMinute: string; endHour: string; endMinute: string }>
   */
  public updatePerDayHour(day: DayWeekShort, patch: Partial<{ startHour: string; startMinute: string; endHour: string; endMinute: string }>): void {
    const map = { ...this.perDayHours() }
    map[day] = { ...map[day], ...patch }
    this.perDayHours.set(map)
    if (this.isIndefinite() && this.selectedWeekDays().includes(day)) {
      this._scheduleIndefiniteBulkSave(true)
      return
    }
    if (this.showWeeklyHoursEditor() && this.openingHoursMode() === 'different' && this.selectedWeekDays().includes(day)) {
      this._scheduleFiniteWeeklyBulkSave({ validateTimes: true, day })
    }
  }

  /**
   * @description Handle date range selection.
   * @param range { startDate: string | null; endDate: string | null }
   */
  public onDateRangeSelected(range: { startDate: string | null; endDate: string | null }): void {
    if (!range.startDate) {
      this.dateRange.set(null)
      this.days.set([])
      this.isIndefinite.set(false)
      return
    }

    this.pendingDateRange.set({ startDate: range.startDate, endDate: range.endDate })
    this._modalService.toggleModal(this.confirmMainRangeModalId)
  }

  /**
   * @description Handle time range selection.
   * @param range { startTime: string; endTime: string }
   */
  public onTimeRangeSelected(range: { startTime: string; endTime: string }): void {
    if (!range.startTime || range.startTime === range.endTime) {
      this.timeRange.set(null)
      this.days.set([])
      return
    }
    const current = this.timeRange()
    if (current) {
      this.pendingTimeRange.set(range)
      this._modalService.toggleModal(this.confirmMainRangeModalId)
      return
    }
    this.timeRange.set(range)
    if (!this.isIndefinite() && this.dateRange()?.startDate && this.days().length === 0) {
      this._regenerateDaysFromRange()
    } else {
      this._regenerateSlotsOnly()
    }
    this._setMainRangeOverrideForCurrentEvent()
    this._updateEventTimings()
  }

  /**
   * @description Confirm applying pending main range changes.
   */
  public confirmMainRangeChanges(): void {
    const pendingDate = this.pendingDateRange()
    const pendingTime = this.pendingTimeRange()
    if (!pendingDate && !pendingTime) {
      this._modalService.toggleModal(this.confirmMainRangeModalId)
      return
    }
    const current = this.dateRange()
    const dateChanged: boolean = Boolean(pendingDate && (!current || current.startDate !== pendingDate.startDate || (current.endDate ?? null) !== (pendingDate.endDate ?? null)))
    // Any confirmed date-chip Apply (including same dates) must rebuild days from the default
    // 10:00–22:00 — not the header timeRange, which may reflect edge-day slot edits.
    const confirmingDateRange: boolean = Boolean(pendingDate)
    if (pendingDate) {
      if (dateChanged) {
        this._exceptionsResetPending = true
      }
      this.dateRange.set(pendingDate)
      this.pendingDateRange.set(null)
    }
    if (pendingTime) {
      this.timeRange.set(pendingTime)
      this.pendingTimeRange.set(null)
    }
    const dr = this.dateRange()
    const intendedIndefinite: boolean = Boolean(dr?.startDate) && dr?.endDate == null
    const ev: Event | null = this.event()
    if (ev?.id != null) {
      this._modeOverrideEventId = ev.id
      this._modeOverrideIsIndefinite = intendedIndefinite
    }
    this.isIndefinite.set(intendedIndefinite)
    if (this.isIndefinite()) {
      this.timeRange.set(null)
      this.days.set([])
      if (dr?.startDate) {
        this.apiResetStartDate.set(dr.startDate)
        this.apiResetEndDate.set(null)
      }
      this._seedIndefiniteDefaultsWhenEnablingWeeklyMode()
      this._updateIndefiniteTimings()
    } else {
      if (confirmingDateRange || this.shouldForceDefaultMainTimeRange(dr ?? null)) {
        this.timeRange.set(DEFAULT_MAIN_TIME_RANGE)
      } else {
        this._ensureFiniteDefaultMainTimeRange()
      }
      if (this.dateRange()) {
        this._regenerateDaysFromRange()
      }
      if (this.timeRange()) {
        this._regenerateSlotsOnly()
      }
      if (this.showWeeklyHoursEditor()) {
        this.selectedWeekDays.set([...this.weekDayOptions])
      }
      this._setMainRangeOverrideForCurrentEvent()
      this._updateEventTimings()
    }
  }

  /**
   * @description Cancel pending main range changes.
   */
  public cancelMainRangeChanges(): void {
    this.pendingDateRange.set(null)
    this.pendingTimeRange.set(null)
    this._modalService.toggleModal(this.confirmMainRangeModalId)
  }

  /**
   * @description Toggle a day open/closed in scheduler.
   * @param event { dayId: string; state: EventTimingStatus }
   */
  public onDayToggle(event: { dayId: string; state: EventTimingStatus }): void {
    const orgId = this.organizationId()
    const evId = this.eventId()
    if (orgId == null || evId == null) return
    const day = this.days().find((d) => d.id === event.dayId)
    if (!day) return
    this._beginTimingsApi()
    this.slotAddSavePreview.set(null)
    this.isSavingDay.set(event.dayId)
    const payload = buildUpdateDayPayload(day, event.state, day.info ?? null, day.slots, event.dayId)
    this._eventService
      .updateDayTimings(orgId, evId, event.dayId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._updateDayLocal(event.dayId, (d) => ({
            ...d,
            state: event.state
          }))
          if (event.state === EventTimingStatus.INACTIVE) {
            this._alertDayDeactivated(event.dayId)
          }
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(true)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message || 'Failed to update day'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(false)
        }
      })
  }

  /**
   * @description Shows success toast for day deactivation action.
   * @param dayId - Deactivated day id.
   */
  private _alertDayDeactivated(dayId: string): void {
    const dayIndex = this.days().findIndex((d) => d.id === dayId)
    if (dayIndex < 0) {
      this._alertService.alert(Alert.SUCCESS, 'Day has been deactivated.', ALERT_SUCCESS_TIMINGS_UPDATED_MS)
      return
    }
    const dayNumber = dayIndex + 1
    const day = this.days()[dayIndex]
    const dt = new Date(day.start_date + 'T12:00:00')
    if (Number.isNaN(dt.getTime())) {
      this._alertService.alert(Alert.SUCCESS, `D${dayNumber} has been deactivated.`, ALERT_SUCCESS_TIMINGS_UPDATED_MS)
      return
    }
    const label = dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    this._alertService.alert(Alert.SUCCESS, `D${dayNumber} “${label}” has been deactivated.`, ALERT_SUCCESS_TIMINGS_UPDATED_MS)
  }

  /**
   * @description Persist note update on blur.
   * @param event { dayId: string; info: string | null }
   */
  public onNoteBlur(event: { dayId: string; info: string | null }): void {
    const orgId = this.organizationId()
    const evId = this.eventId()
    if (orgId == null || evId == null) return
    const day = this.days().find((d) => d.id === event.dayId)
    if (!day) return
    const nextNote: string = (event.info ?? '').trim()
    const prevNote: string = (day.info ?? '').trim()
    if (nextNote === prevNote) return
    this._beginTimingsApi()
    this.slotAddSavePreview.set(null)
    this.isSavingDay.set(event.dayId)
    const payload = buildUpdateDayPayload(day, day.state, nextNote || null, day.slots, event.dayId)
    this._eventService
      .updateDayTimings(orgId, evId, event.dayId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._updateDayLocal(event.dayId, (d) => ({ ...d, info: nextNote }))
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(true)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message || 'Failed to update note'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(false)
        }
      })
  }

  /**
   * @description Smallest merged slot that fully contains the added preview interval (for overlay + hide during save).
   */
  private _findSlotCoveringAddedPreview(slots: EventSlot[], preview: { from: string; to: string }): EventSlot | null {
    const pf: number = parseTimeToHours(preview.from.trim().slice(0, 5))
    const pt: number = parseTimeToHours(preview.to.trim().slice(0, 5))
    if (!(pf < pt)) return null
    let best: EventSlot | null = null
    let bestSpan: number = Number.POSITIVE_INFINITY
    for (const s of slots) {
      const fromRaw: string = (s.from ?? '').trim().slice(0, 5)
      const toRaw: string = (s.to ?? '').trim().slice(0, 5)
      const toNorm: string = toRaw === '24:00' || toRaw.startsWith('24:') ? '23:59' : toRaw
      const sf: number = parseTimeToHours(fromRaw)
      const st: number = parseTimeToHours(toNorm)
      if (sf <= pf && st >= pt) {
        const span: number = st - sf
        if (span < bestSpan) {
          bestSpan = span
          best = s
        }
      }
    }
    return best
  }

  /**
   * @description Persist slot changes from scheduler.
   * @param event `dayId`, `slots`, optional `addedSlotPreview` for add-slot loading UI on that range.
   * @param onSuccess Optional callback invoked after a successful save.
   */
  public onSlotsChange(event: { dayId: string; slots: EventSlot[]; addedSlotPreview?: { from: string; to: string } }, onSuccess?: () => void): void {
    const orgId = this.organizationId()
    const evId = this.eventId()
    if (orgId == null || evId == null) return
    const day = this.days().find((d) => d.id === event.dayId)
    if (!day) return
    const previousTimeRange = this.timeRange()
    const shouldSyncTimeRangeFromEdgeChange = this._shouldSyncEventTimeRangeFromSchedulerEdgeSlots() && this._isFirstOrLastDay(event.dayId)
    const previousSlots: EventSlot[] = day.slots.map((s) => ({ ...s, from: s.from, to: s.to, label: s.label ?? '' }))
    const nextSlots: EventSlot[] = sortSlotsByTime(event.slots.map((s) => ({ ...s, from: s.from, to: s.to, label: s.label ?? '' })))
    const addPreview = event.addedSlotPreview
    if (addPreview) {
      const merged: EventSlot | null = this._findSlotCoveringAddedPreview(nextSlots, addPreview)
      const overlayFrom: string = this.normalizeHm((merged?.from ?? addPreview.from).trim().slice(0, 5))
      const overlayTo: string = this.normalizeHm((merged?.to ?? addPreview.to).trim().slice(0, 5))
      this.slotAddSavePreview.set({ dayId: event.dayId, overlayFrom, overlayTo })
    } else {
      this.slotAddSavePreview.set(null)
    }
    this._updateDayLocal(event.dayId, (d) => ({ ...d, slots: nextSlots }))
    this._beginTimingsApi()
    this.isSavingDay.set(event.dayId)
    const payload = buildUpdateDayPayload(day, day.state, day.info ?? null, event.slots, event.dayId)
    this._eventService
      .updateDayTimings(orgId, evId, event.dayId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._updateDayLocal(event.dayId, (d) => ({ ...d, slots: nextSlots }))
          const needsChainedPublicSync = this.isFiniteRange30OrLess() && shouldSyncTimeRangeFromEdgeChange
          if (needsChainedPublicSync) {
            const scheduled = this._syncPublicDatesAfterEdgeDaySlotChange(event.dayId, {
              chainAfterDayTimingsSave: true,
              onChainedComplete: () => {
                this.isSavingDay.set(null)
                this.slotAddSavePreview.set(null)
                onSuccess?.()
              }
            })
            if (!scheduled) {
              if (shouldSyncTimeRangeFromEdgeChange) {
                this._syncEventTimeRangeFromSchedulerEdgeSlots(this.days())
              } else if (previousTimeRange) {
                this.timeRange.set(previousTimeRange)
              }
              this.isSavingDay.set(null)
              this.slotAddSavePreview.set(null)
              this._endTimingsApi(true)
              onSuccess?.()
            }
            return
          }
          if (shouldSyncTimeRangeFromEdgeChange) {
            this._syncEventTimeRangeFromSchedulerEdgeSlots(this.days())
          } else if (previousTimeRange) {
            this.timeRange.set(previousTimeRange)
          }
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(true)
          onSuccess?.()
        },
        error: (err: unknown) => {
          this._updateDayLocal(event.dayId, (d) => ({ ...d, slots: previousSlots }))
          const msg = (err as { message?: string } | null)?.message || 'Failed to update slots'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(false)
        }
      })
  }

  /**
   * @description Open add-slot modal for given day.
   * @param event { dayId: string }
   */
  public onAddSlotModalOpen(event: { dayId: string }): void {
    this.addModalDayId.set(event.dayId)
    this.addModalStartHour.set('00')
    this.addModalStartMinute.set('00')
    this.addModalEndHour.set('01')
    this.addModalEndMinute.set('00')
    this._modalService.toggleModal(this.addDaySlotModalId)
  }

  /**
   * @description Confirm add-slot modal.
   */
  public confirmAddSlotModal(): void {
    const dayId = this.addModalDayId()
    if (!dayId) return
    const startStr = `${this.addModalStartHour()}:${this.addModalStartMinute()}`
    const endStr = `${this.addModalEndHour()}:${this.addModalEndMinute()}`
    const openH = parseTimeToHours(startStr)
    const closeH = parseTimeToHours(endStr)
    if (!(openH < closeH)) {
      this._alertService.alert(Alert.ERROR, 'Opening time must be before closing time.')
      return
    }
    if (closeH - openH < MIN_SLOT_DURATION_H) {
      this._alertService.alert(Alert.ERROR, 'A time slot must be at least 1 hour.')
      return
    }
    const day = this.days().find((d) => d.id === dayId)
    if (!day) return
    const merged = mergeTimeSlots([...day.slots, { from: startStr, to: endStr, label: '' }])
    this.onSlotsChange({ dayId, slots: merged, addedSlotPreview: { from: startStr.slice(0, 5), to: endStr.slice(0, 5) } }, () =>
      this._modalService.toggleModal(this.addDaySlotModalId)
    )
  }

  /**
   * @description Add slot on scheduler click (desktop).
   * @param event { dayId: string; startTime: string }
   */
  public onAddSlot(event: { dayId: string; startTime: string }): void {
    const day = this.days().find((d) => d.id === event.dayId)
    if (!day) return
    const [h, m] = event.startTime.split(':').map(Number)
    const startMin = (h ?? 0) * 60 + (m ?? 0)
    const endMin = Math.min(23 * 60 + 59, startMin + 60)
    const eh = Math.floor(endMin / 60)
    const em = endMin % 60
    const endTime = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`
    const merged = mergeTimeSlots([...day.slots, { from: event.startTime, to: endTime, label: '' }])
    this.onSlotsChange({
      dayId: event.dayId,
      slots: merged,
      addedSlotPreview: { from: event.startTime.trim().slice(0, 5), to: endTime.slice(0, 5) }
    })
  }

  /**
   * @description Remove slot by index.
   * @param event { dayId: string; slotIndex: number }
   */
  public onSlotRemove(event: { dayId: string; slotIndex: number }): void {
    const day = this.days().find((d) => d.id === event.dayId)
    if (!day) return
    if ((day.slots ?? []).length <= 1) {
      this._closeDayAfterRemovingLastSlot(day)
      return
    }
    const newSlots = day.slots.filter((_, i) => i !== event.slotIndex)
    this.onSlotsChange({ dayId: event.dayId, slots: newSlots })
  }

  /**
   * @description When the user removes the only slot for a day, keep that slot and set the day to closed.
   * @param day - Day being updated.
   */
  private _closeDayAfterRemovingLastSlot(day: EventDay): void {
    const orgId = this.organizationId()
    const evId = this.eventId()
    if (orgId == null || evId == null) return
    this._beginTimingsApi()
    this.slotAddSavePreview.set(null)
    this.isSavingDay.set(day.id)
    const slotsToKeep = day.slots ?? []
    const payload = buildUpdateDayPayload(day, EventTimingStatus.INACTIVE, day.info ?? null, slotsToKeep, day.id)
    this._eventService
      .updateDayTimings(orgId, evId, day.id, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._updateDayLocal(day.id, (d) => ({ ...d, state: EventTimingStatus.INACTIVE, slots: slotsToKeep }))
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(true)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message || 'Failed to update day'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingDay.set(null)
          this.slotAddSavePreview.set(null)
          this._endTimingsApi(false)
        }
      })
  }

  /**
   * @description Open edit-slot modal.
   * @param event { dayId: string; slotIndex: number }
   */
  public onSlotEditOpen(event: { dayId: string; slotIndex: number }): void {
    const day = this.days().find((d) => d.id === event.dayId)
    const slot = day?.slots[event.slotIndex]
    if (!day || !slot) return
    const start = slot.from.slice(0, 5)
    let end = slot.to.slice(0, 5)
    if (end === '24:00' || end.startsWith('24:')) end = '23:59'
    this.editModalDayId.set(event.dayId)
    this.editModalSlotIndex.set(event.slotIndex)
    this.editModalStartTime.set(start)
    this.editModalEndTime.set(end)
    this._applyEditModalTimeStringsToSelects()
    this.editModalScope.set('day')
    this.editModalSnapshot.set({ start, end, scope: 'day' })
    this._modalService.toggleModal(this.editDayHoursModalId)
  }

  /**
   * @description Update edit modal time fields from selects.
   */
  public onEditModalTimeFieldChange(): void {
    this.editModalStartTime.set(`${this.editModalStartHour()}:${this.editModalStartMinute()}`)
    this.editModalEndTime.set(`${this.editModalEndHour()}:${this.editModalEndMinute()}`)
  }

  /**
   * @description Opening hour change from edit-slot modal.
   * @param h string
   */
  public onEditModalStartHourChange(h: string): void {
    this.editModalStartHour.set(h)
    this.onEditModalTimeFieldChange()
  }

  /**
   * @description Opening minute change from edit-slot modal.
   * @param m string
   */
  public onEditModalStartMinuteChange(m: string): void {
    this.editModalStartMinute.set(m)
    this.onEditModalTimeFieldChange()
  }

  /**
   * @description Closing hour change from edit-slot modal.
   * @param h string
   */
  public onEditModalEndHourChange(h: string): void {
    this.editModalEndHour.set(h)
    this.onEditModalTimeFieldChange()
  }

  /**
   * @description Closing minute change from edit-slot modal.
   * @param m string
   */
  public onEditModalEndMinuteChange(m: string): void {
    this.editModalEndMinute.set(m)
    this.onEditModalTimeFieldChange()
  }

  /**
   * @description Opening hour change from add-slot modal.
   * @param h string
   */
  public onAddModalStartHourChange(h: string): void {
    this.addModalStartHour.set(h)
  }

  /**
   * @description Opening minute change from add-slot modal.
   * @param m string
   */
  public onAddModalStartMinuteChange(m: string): void {
    this.addModalStartMinute.set(m)
  }

  /**
   * @description Closing hour change from add-slot modal.
   * @param h string
   */
  public onAddModalEndHourChange(h: string): void {
    this.addModalEndHour.set(h)
  }

  /**
   * @description Closing minute change from add-slot modal.
   * @param m string
   */
  public onAddModalEndMinuteChange(m: string): void {
    this.addModalEndMinute.set(m)
  }

  /**
   * @description Reset edit modal state back to snapshot.
   */
  public resetEditModal(): void {
    if (this.isSavingEditModal()) return
    const snap = this.editModalSnapshot()
    if (!snap) return
    this.editModalStartTime.set(snap.start)
    this.editModalEndTime.set(snap.end)
    this._applyEditModalTimeStringsToSelects()
    this.editModalScope.set(snap.scope)
  }

  /**
   * @description Persist edit modal changes.
   */
  public saveEditModal(): void {
    if (this.isSavingEditModal()) return
    this.onEditModalTimeFieldChange()
    const startStr = `${this.editModalStartHour()}:${this.editModalStartMinute()}`
    const endStr = `${this.editModalEndHour()}:${this.editModalEndMinute()}`
    const openH = parseTimeToHours(startStr)
    const closeH = parseTimeToHours(endStr)
    if (!(openH < closeH)) {
      this._alertService.alert(Alert.ERROR, 'Opening time must be before closing time.')
      return
    }
    const isEndOfDayException = startStr === END_OF_DAY_EDIT_SLOT_FROM && endStr === END_OF_DAY_EDIT_SLOT_TO
    if (!isEndOfDayException && closeH - openH < MIN_SLOT_DURATION_H) {
      this._alertService.alert(Alert.ERROR, 'A time slot must be at least 1 hour.')
      return
    }
    const scope = this.editModalScope()
    const dayId = this.editModalDayId()
    const slotIndex = this.editModalSlotIndex()
    if (dayId == null || slotIndex == null) return
    if (scope === 'entire') {
      this.isSavingEditModal.set(true)
      const editedDay = this.days().find((d) => d.id === dayId)
      const editedLabel = editedDay?.slots[slotIndex]?.label ?? ''
      // Replace hours on every day (appending merges into existing ranges and no-ops).
      const nextDays: EventDay[] = this.days().map((d) => ({
        ...d,
        slots: [{ from: startStr, to: endStr, label: editedLabel }]
      }))
      this.days.set(nextDays)
      this._syncEventTimeRangeFromSchedulerEdgeSlots(nextDays)
      const orgId = this.organizationId()
      const evId = this.eventId()
      const payload = this._buildUpdateTimingsPayload()
      if (orgId == null || evId == null || !payload) {
        this.isSavingEditModal.set(false)
        this._alertService.alert(Alert.ERROR, 'Could not build timings to save.')
        return
      }
      this._beginTimingsApi()
      this._eventService
        .updateTimings(orgId, evId, payload)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (res) => {
            const data = res?.data as EventTimingsResponse | undefined
            if (data) {
              this._applyTimingsResponse(data)
            }
            this.isSavingEditModal.set(false)
            this._endTimingsApi(true)
            this._modalService.toggleModal(this.editDayHoursModalId)
          },
          error: (err: unknown) => {
            const msg = (err as { message?: string } | null)?.message ?? 'Failed to update timings'
            this._alertService.alert(Alert.ERROR, msg)
            this.isSavingEditModal.set(false)
            this._endTimingsApi(false)
          }
        })
      return
    }
    const day = this.days().find((d) => d.id === dayId)
    if (!day) return
    const label = day.slots[slotIndex]?.label ?? ''
    const nextSlots = day.slots.map((s, i) => (i === slotIndex ? { ...s, from: startStr, to: endStr, label } : s))
    const merged = mergeTimeSlots(nextSlots)
    const orgId = this.organizationId()
    const evId = this.eventId()
    if (orgId == null || evId == null) return
    this._beginTimingsApi()
    this.isSavingEditModal.set(true)
    const payload = buildUpdateDayPayload(day, day.state, day.info ?? null, merged, dayId)
    this._eventService
      .updateDayTimings(orgId, evId, dayId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._updateDayLocal(dayId, (d) => ({ ...d, slots: merged }))
          this.isSavingEditModal.set(false)
          this._endTimingsApi(true)
          this._syncPublicDatesAfterEdgeDaySlotChange(dayId)
          this._modalService.toggleModal(this.editDayHoursModalId)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message || 'Failed to update hours'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingEditModal.set(false)
          this._endTimingsApi(false)
        }
      })
  }

  private _beginTimingsApi(): void {
    this._timingsApiInflight++
    this.isSavingTimings.set(true)
  }

  private _endTimingsApi(success: boolean): void {
    this._timingsApiInflight = Math.max(0, this._timingsApiInflight - 1)
    if (this._timingsApiInflight === 0) {
      this.isSavingTimings.set(false)
      if (success) this.showTimingsSavedIndicator.set(true)
    }
  }

  private _computeMainRangeKey(dateRange: { startDate: string; endDate: string | null } | null, timeRange: { startTime: string; endTime: string } | null): string | null {
    if (!dateRange?.startDate || !timeRange?.startTime || !timeRange?.endTime) return null
    return `${dateRange.startDate}|${dateRange.endDate ?? ''}|${timeRange.startTime.slice(0, 5)}|${timeRange.endTime.slice(0, 5)}`
  }

  private _setMainRangeOverrideForCurrentEvent(): void {
    const evId = this.event()?.id ?? null
    if (evId == null) return
    const key = this._computeMainRangeKey(this.dateRange(), this.timeRange())
    this._mainRangeOverrideEventId = evId
    this._mainRangeOverrideKey = key
  }

  private _datesInRange(start: string, end: string): string[] {
    const out: string[] = []
    const s = new Date(start + 'T12:00:00')
    const e = new Date(end + 'T12:00:00')
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return out
    const cur = new Date(s)
    while (cur <= e) {
      out.push(cur.toISOString().slice(0, 10))
      cur.setDate(cur.getDate() + 1)
    }
    return out
  }

  private _buildDaysFromRange(): EventDay[] {
    const dr = this.dateRange()
    const tr = this.timeRange()
    if (!dr?.startDate || !tr) return []
    const startTime = tr.startTime ?? '00:00'
    const endTime = tr.endTime ?? '23:59'
    const end = dr.endDate ?? dr.startDate
    const dates = this._datesInRange(dr.startDate, end)
    return dates.map((date) => ({
      id: date,
      start_date: date,
      end_date: date,
      info: '',
      state: EventTimingStatus.ACTIVE,
      slots: [{ from: startTime, to: endTime, label: '' }]
    }))
  }

  private _regenerateDaysFromRange(): void {
    this.days.set(this._buildDaysFromRange())
  }

  private _ensureFiniteDefaultMainTimeRange(): void {
    if (this.isIndefinite()) return
    if (!this.dateRange()?.startDate) return
    if (this.timeRange()) return
    this.timeRange.set(DEFAULT_MAIN_TIME_RANGE)
  }

  private _hydrateFiniteWeeklyUiFromDays(): void {
    if (this.isIndefinite() || !this.showWeeklyHoursEditor()) return
    const list: EventDay[] = this.days()
    if (!list.length) return

    const defaultRange = { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' } as const
    const slotKey = (from: string, to: string): string => `${from.slice(0, 5)}|${to.slice(0, 5)}`
    const weekdayFirstRange = new Map<DayWeekShort, { from: string; to: string }>()

    for (const d of list) {
      if (weekdayFirstRange.size >= 7) break
      if (d.state === EventTimingStatus.INACTIVE) continue
      const wd = this._dayWeekShortFromYmd(d.start_date)
      if (!wd || weekdayFirstRange.has(wd)) continue
      const s0 = d.slots?.[0]
      if (!s0?.from || !s0?.to) continue
      weekdayFirstRange.set(wd, { from: s0.from, to: s0.to })
    }

    const selected: DayWeekShort[] = this.selectedWeekDays()
    const keys: string[] = []
    for (const wd of selected) {
      const r = weekdayFirstRange.get(wd)
      if (!r) continue
      keys.push(slotKey(r.from, r.to))
    }
    const unique = Array.from(new Set(keys))
    const mode: 'same' | 'different' = unique.length > 1 ? 'different' : 'same'
    this.openingHoursMode.set(mode)

    const nextPerDay = { ...this.perDayHours() }
    for (const wd of this.weekDayOptions) {
      const r = weekdayFirstRange.get(wd)
      if (r) {
        const from = r.from.slice(0, 5)
        const toRaw = r.to.slice(0, 5)
        const to = toRaw === '24:00' || toRaw.startsWith('24:') ? '23:59' : toRaw
        const [sh, sm] = from.split(':')
        const [eh, em] = to.split(':')
        nextPerDay[wd] = {
          startHour: padHourPart(sh ?? defaultRange.startHour),
          startMinute: clampMinuteToken(sm ?? defaultRange.startMinute),
          endHour: padHourPart(eh ?? defaultRange.endHour),
          endMinute: clampMinuteToken(em ?? defaultRange.endMinute)
        }
      } else {
        nextPerDay[wd] = { ...defaultRange }
      }
    }
    this.perDayHours.set(nextPerDay)

    if (mode === 'same') {
      const ref = unique[0]
      const [from, to] = (ref ?? '').split('|')
      const fromHm = from && /^\d{2}:\d{2}$/.test(from) ? from : `${defaultRange.startHour}:${defaultRange.startMinute}`
      const toHm = to && /^\d{2}:\d{2}$/.test(to) ? to : `${defaultRange.endHour}:${defaultRange.endMinute}`
      const [sh, sm] = fromHm.split(':')
      const [eh, em] = toHm.split(':')
      this.sameHoursStartHour.set(padHourPart(sh ?? defaultRange.startHour))
      this.sameHoursStartMinute.set(clampMinuteToken(sm ?? defaultRange.startMinute))
      this.sameHoursEndHour.set(padHourPart(eh ?? defaultRange.endHour))
      this.sameHoursEndMinute.set(clampMinuteToken(em ?? defaultRange.endMinute))
    }
  }

  private _regenerateSlotsOnly(): void {
    const tr = this.timeRange()
    const currentDays = this.days()
    if (!tr || currentDays.length === 0) return
    const startTime = tr.startTime ?? '00:00'
    const endTime = tr.endTime ?? '23:59'
    const newSlot: EventSlot = { from: startTime, to: endTime, label: '' }
    this.days.set(currentDays.map((d) => ({ ...d, slots: [newSlot] })))
  }

  private _buildUpdateTimingsPayload(): UpdateEventTimingsPayload | null {
    const dr = this.dateRange()
    const tr = this.timeRange()
    const dayList = this.days()
    if (!dr?.startDate || !tr || dayList.length === 0) return null
    const endCal = dr.endDate ?? dr.startDate
    const baseStartTime = (tr.startTime ?? '00:00').slice(0, 5)
    const baseEndTime = (tr.endTime ?? '23:59').slice(0, 5)
    const publicOverride = this.getPublicTimeOverrideForFiniteEdgeSlots({ dateRange: dr, timeRange: tr, days: dayList })
    const publicStartTime = publicOverride?.publicStartTime ?? baseStartTime
    const publicEndTime = publicOverride?.publicEndTime ?? baseEndTime
    return {
      is_indefinitely: false,
      public_start_date: formatPublicDateTimeForTimingsPayload(dr.startDate, publicStartTime),
      public_end_date: formatPublicDateTimeForTimingsPayload(endCal, publicEndTime),
      timings: dayList.map((day) => ({
        start_date: day.start_date,
        end_date: day.end_date,
        info: day.info ?? '',
        state: day.state,
        slots: day.slots.map((s) => ({ from: s.from, to: s.to, label: s.label ?? '' }))
      }))
    }
  }

  private _applyTimingsResponse(data: EventTimingsResponse): void {
    if (data.timings?.length) {
      this.days.set(data.timings.map(normalizeApiTimingDay))
      const ev = this.event()
      if (ev && !this.isIndefinite()) {
        this._lastFiniteTimingsHydrationKey = finiteTimingsHydrationKey(ev.id, data.timings)
      }
    }
    const parsed = parsePublicRangeFromApi(data.public_start_date, data.public_end_date)
    if (parsed) {
      this.dateRange.set({ startDate: parsed.startDate, endDate: parsed.endDate })
      this.timeRange.set({ startTime: parsed.startTime.slice(0, 5), endTime: parsed.endTime.slice(0, 5) })
    }
  }

  private _updateEventTimings(): void {
    const orgId = this.organizationId()
    const evId = this.eventId()
    const payload = this._buildUpdateTimingsPayload()
    if (orgId == null || evId == null || !payload) return
    this._beginTimingsApi()
    this._eventService
      .updateTimings(orgId, evId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const data = res?.data as EventTimingsResponse | undefined
          if (data) {
            this._applyTimingsResponse(data)
          }
          this._resetTimingExceptionsIfPending(orgId, evId)
          this._endTimingsApi(true)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message ?? 'Failed to update timings'
          this._alertService.alert(Alert.ERROR, msg)
          this._endTimingsApi(false)
        }
      })
  }

  /**
   * @description Returns true when the given day id is the first or last day in the current finite list.
   * @param dayId - Day id to test.
   * @returns Whether this day is on the event range edge.
   */
  private _isFirstOrLastDay(dayId: string): boolean {
    const list = this.days()
    if (!list.length) return false
    return list[0]?.id === dayId || list[list.length - 1]?.id === dayId
  }

  /**
   * @description For finite ranges of 30 days or less, sync public start/end date times after
   * edge-day slot edits (single-day API flow) so public dates follow earliest/latest edge slots.
   * @param dayId - Updated day id.
   * @param opts When `chainAfterDayTimingsSave` is true, skips a duplicate timings-save counter bump
   *   because the caller already opened one for the preceding `updateDayTimings` request; call
   *   `onChainedComplete` after that follow-up request settles so day-level loaders can clear in sync.
   * @returns Whether an `updateEventData` request was scheduled.
   */
  private _syncPublicDatesAfterEdgeDaySlotChange(dayId: string, opts?: { chainAfterDayTimingsSave?: boolean; onChainedComplete?: () => void }): boolean {
    const finite30 = this.isFiniteRange30OrLess()
    const firstOrLast = this._isFirstOrLastDay(dayId)
    if (!finite30) return false
    if (!firstOrLast) return false
    const orgId = this.organizationId()
    const evId = this.eventId()
    const payload = this._buildPublicDatePatchForEdgeDayChange(dayId)
    if (orgId == null || evId == null || !payload) return false
    if (!opts?.chainAfterDayTimingsSave) {
      this._beginTimingsApi()
    }
    this._eventService
      .updateEventData(orgId, evId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._syncTimeRangeWithEdgeDayChange(dayId)
          this._setMainRangeOverrideForCurrentEvent()
          this._endTimingsApi(true)
          opts?.onChainedComplete?.()
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message ?? 'Failed to sync public event dates'
          this._alertService.alert(Alert.ERROR, msg)
          this._endTimingsApi(false)
          opts?.onChainedComplete?.()
        }
      })
    return true
  }

  private _syncFiniteSelectedWeekDaysFromDays(): void {
    if (this.isIndefinite() || !this.showWeeklyHoursEditor()) return
    const list = this.days()
    if (!list.length) return
    const allowed = new Set<DayWeekShort>()
    for (const r of list) {
      if (r.state === EventTimingStatus.INACTIVE) continue
      const dw = this._dayWeekShortFromYmd(r.start_date)
      if (dw) allowed.add(dw)
    }
    const ordered = this.weekDayOptions.filter((d) => allowed.has(d))
    this.selectedWeekDays.set(ordered)
  }

  /**
   * @description Applies a finite weekday status to the given day.
   * @param day - The day to apply the status to.
   * @param rowState - The status to apply to the day.
   */
  private _applyFiniteWeekdayStatus(day: DayWeekShort, rowState: EventTimingStatus): void {
    if (this.isIndefinite()) return
    if (!this.showWeeklyHoursEditor()) return
    const dr = this.dateRange()
    if (!dr?.startDate) return
    this._ensureFiniteDefaultMainTimeRange()
    if (this.days().length === 0 && this.timeRange()) {
      this._regenerateDaysFromRange()
    }
    const list = this.days()
    if (list.length === 0) return
    const nextDays = list.map((d) => {
      const dw = this._dayWeekShortFromYmd(d.start_date)
      if (dw !== day) return d
      return { ...d, state: rowState }
    })
    this.days.set(nextDays)
    this._setMainRangeOverrideForCurrentEvent()
    this._updateEventTimings()
  }

  private _finiteWeeklyTimesValid(opts: { day?: DayWeekShort }): boolean {
    if (this.openingHoursMode() === 'same') {
      const endH = parseTimeToHours(`${this.sameHoursEndHour()}:${this.sameHoursEndMinute()}`)
      const startH = parseTimeToHours(`${this.sameHoursStartHour()}:${this.sameHoursStartMinute()}`)
      return endH > startH
    }
    const d = opts.day
    if (!d) return true
    const t = this.perDayHours()[d]
    const endH = parseTimeToHours(`${t.endHour}:${t.endMinute}`)
    const startH = parseTimeToHours(`${t.startHour}:${t.startMinute}`)
    return endH > startH
  }

  private _scheduleFiniteWeeklyBulkSave(opts: { validateTimes: boolean; day?: DayWeekShort }): void {
    if (this._finiteWeeklyBulkTimer) clearTimeout(this._finiteWeeklyBulkTimer)
    this._finiteWeeklyBulkTimer = setTimeout(() => {
      this._finiteWeeklyBulkTimer = null
      if (this.isIndefinite() || !this.showWeeklyHoursEditor()) return
      const dr = this.dateRange()
      if (!dr?.startDate) return
      if (opts.validateTimes && !this._finiteWeeklyTimesValid({ day: opts.day })) return
      this._applyFiniteWeeklyHoursToDays(opts.day ?? null)
    }, 450)
  }

  private _applyFiniteWeeklyHoursToDays(day: DayWeekShort | null): void {
    if (this.isIndefinite() || !this.showWeeklyHoursEditor()) return
    this._ensureFiniteDefaultMainTimeRange()
    if (this.days().length === 0 && this.timeRange()) {
      this._regenerateDaysFromRange()
    }
    const list = this.days()
    if (list.length === 0) return
    const applySlots = (slots: EventSlot[], start: string, end: string): EventSlot[] => {
      return (slots ?? []).map((s) => ({ ...s, from: start, to: end }))
    }
    let nextDays: EventDay[] = list
    if (this.openingHoursMode() === 'same') {
      const start = `${padHourPart(this.sameHoursStartHour())}:${clampMinuteToken(this.sameHoursStartMinute())}`
      const end = `${padHourPart(this.sameHoursEndHour())}:${clampMinuteToken(this.sameHoursEndMinute())}`
      this.timeRange.set({ startTime: start, endTime: end })
      nextDays = list.map((d) => ({ ...d, slots: applySlots(d.slots, start, end) }))
    } else if (day) {
      const t = this.perDayHours()[day]
      const start = `${padHourPart(t.startHour)}:${clampMinuteToken(t.startMinute)}`
      const end = `${padHourPart(t.endHour)}:${clampMinuteToken(t.endMinute)}`
      nextDays = list.map((d) => {
        const dw = this._dayWeekShortFromYmd(d.start_date)
        if (dw !== day) return d
        return { ...d, slots: applySlots(d.slots, start, end) }
      })
    } else {
      return
    }
    this.days.set(nextDays)
    this._setMainRangeOverrideForCurrentEvent()
    this._updateEventTimings()
  }

  private _scheduleIndefiniteBulkSave(validateTimes: boolean): void {
    if (this._indefiniteBulkTimer) clearTimeout(this._indefiniteBulkTimer)
    this._indefiniteBulkTimer = setTimeout(() => {
      this._indefiniteBulkTimer = null
      if (!this.isIndefinite()) return
      const dr = this.dateRange()
      if (!dr?.startDate) return
      if (validateTimes && !this._indefiniteDayTimesValid()) return
      this._updateIndefiniteTimings()
    }, 450)
  }

  private _indefiniteDayTimesValid(): boolean {
    const selected = this.selectedWeekDays()
    if (selected.length === 0) return true
    if (this.openingHoursMode() === 'same') {
      const endH = parseTimeToHours(`${this.sameHoursEndHour()}:${this.sameHoursEndMinute()}`)
      const startH = parseTimeToHours(`${this.sameHoursStartHour()}:${this.sameHoursStartMinute()}`)
      return endH > startH
    }
    for (const d of selected) {
      const t = this.perDayHours()[d]
      if (parseTimeToHours(`${t.endHour}:${t.endMinute}`) <= parseTimeToHours(`${t.startHour}:${t.startMinute}`)) {
        return false
      }
    }
    return true
  }

  private _buildIndefiniteTimingsPayload(): UpdateEventTimingsPayload | null {
    const orgId = this.organizationId()
    const evId = this.eventId()
    const dr = this.dateRange()
    if (orgId == null || evId == null) return null
    const startDate = dr?.startDate ?? null
    if (!startDate) return null
    const existingPublicEnd = parsePublicDateTimeFromApi(this.event()?.public_end_date ?? '').date
    const selected = this.selectedWeekDays()
    const mode = this.openingHoursMode()
    const perDay = this.perDayHours()
    const meta = this.indefiniteApiByDay()
    const slotRangeForDay = (day: DayWeekShort): { from: string; to: string } => {
      const t =
        mode === 'same'
          ? { startHour: this.sameHoursStartHour(), startMinute: this.sameHoursStartMinute(), endHour: this.sameHoursEndHour(), endMinute: this.sameHoursEndMinute() }
          : perDay[day]
      const from = `${t.startHour}:${t.startMinute}`
      let to = `${t.endHour}:${t.endMinute}`
      if (to === '24:00' || to.startsWith('24:')) to = '23:59'
      return { from, to }
    }
    return {
      is_indefinitely: true,
      public_start_date: formatPublicDateTimeForTimingsPayload(startDate, DEFAULT_MAIN_TIME_RANGE.startTime),
      public_end_date: existingPublicEnd ? formatPublicDateTimeForTimingsPayload(existingPublicEnd, DEFAULT_MAIN_TIME_RANGE.endTime) : undefined,
      timings: selected.map((day) => {
        const dayMeta = meta[day]
        const { from, to } = slotRangeForDay(day)
        return {
          id: dayMeta?.timingId ?? undefined,
          start_date: startDate,
          day_week: day,
          info: dayMeta?.info ?? '',
          state: EventTimingStatus.ACTIVE,
          slots: [{ from, to, label: dayMeta?.label ?? '' }]
        }
      })
    }
  }

  private _updateIndefiniteTimings(): void {
    if (this.isSavingIndefinite()) return
    const orgId = this.organizationId()
    const evId = this.eventId()
    const payload = this._buildIndefiniteTimingsPayload()
    if (orgId == null || evId == null || !payload) return
    this._beginTimingsApi()
    this.isSavingIndefinite.set(true)
    this._eventService
      .updateTimings(orgId, evId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const data = res?.data as EventTimingsResponse | undefined
          if (data?.timings) {
            this._syncIndefiniteMetaFromApiRows(data.timings)
            const start = this.dateRange()?.startDate ?? null
            this.apiResetStartDate.set(start)
            this.apiResetEndDate.set(null)
          }
          this.isSavingIndefinite.set(false)
          if (orgId != null && evId != null) {
            this._resetTimingExceptionsIfPending(orgId, evId)
          }
          this._endTimingsApi(true)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message ?? 'Failed to update timings'
          this._alertService.alert(Alert.ERROR, msg)
          this.isSavingIndefinite.set(false)
          this._endTimingsApi(false)
        }
      })
  }

  private _seedIndefiniteDefaultsWhenEnablingWeeklyMode(): void {
    const defaultDays: DayWeekShort[] = [...this.weekDayOptions]
    this.selectedWeekDays.set(defaultDays)
    this.openingHoursMode.set('same')
    this.sameHoursStartHour.set('10')
    this.sameHoursStartMinute.set('00')
    this.sameHoursEndHour.set('22')
    this.sameHoursEndMinute.set('00')
    const map = { ...this.perDayHours() }
    for (const d of this.weekDayOptions) {
      map[d] = { startHour: '10', startMinute: '00', endHour: '22', endMinute: '00' }
    }
    this.perDayHours.set(map)
  }

  private _syncIndefiniteMetaFromApiRows(rows: EventTimings[]): void {
    if (!rows?.length) return
    const meta = { ...this.indefiniteApiByDay() }
    for (const row of rows) {
      const d = normalizeDayWeekShort(row.day_week)
      if (!d) continue
      const slot0 = firstTimingSlot(row.slots)
      meta[d] = {
        timingId: row.id != null ? String(row.id) : meta[d]?.timingId ?? null,
        info: row.info || meta[d]?.info || '',
        label: (slot0?.label ?? meta[d]?.label ?? '').trim()
      }
    }
    this.indefiniteApiByDay.set(meta)
  }

  private _prefillIndefiniteWeeklyFromApi(rows: EventTimings[], publicStartIso?: string, eventTimezone?: string | null): void {
    const weekRows: EventTimings[] = []
    for (const row of rows ?? []) {
      const dayWeek = normalizeDayWeekShort(row.day_week)
      if (!dayWeek) continue
      weekRows.push({ ...row, day_week: dayWeek })
    }
    const selected = weekRows.filter((r) => r.state !== EventTimingStatus.INACTIVE).map((r) => r.day_week as DayWeekShort)
    const ordered = this.weekDayOptions.filter((d) => selected.includes(d))
    this.selectedWeekDays.set(ordered)
    const map = { ...this.perDayHours() }
    const meta = { ...this.indefiniteApiByDay() }
    for (const row of weekRows) {
      const day = row.day_week as DayWeekShort
      const slot0 = firstTimingSlot(row.slots)
      meta[day] = { timingId: row.id ? String(row.id) : null, info: row.info, label: (slot0?.label ?? '').trim() }
      const times = perDayHoursFromFirstSlot(row.slots, eventTimezone)
      if (times) {
        map[day] = coerceIndefiniteWeeklyRange(times)
      }
    }
    this.perDayHours.set(map)
    this.indefiniteApiByDay.set(meta)
    if (ordered.length > 0) {
      const mode = indefiniteOpeningHoursModeFromWeekRows(weekRows, eventTimezone)
      this.openingHoursMode.set(mode)
      let templateTimes: NonNullable<ReturnType<typeof perDayHoursFromFirstSlot>> | null = null
      for (const dayKey of ordered) {
        const row = weekRows.find((r) => (r.day_week as DayWeekShort) === dayKey)
        const t = perDayHoursFromFirstSlot(row?.slots, eventTimezone)
        if (t) {
          templateTimes = t
          break
        }
      }
      if (!templateTimes) {
        for (const r of weekRows) {
          const t = perDayHoursFromFirstSlot(r.slots, eventTimezone)
          if (t) {
            templateTimes = t
            break
          }
        }
      }
      const resolvedSameHours = coerceIndefiniteWeeklyRange(templateTimes ?? map[ordered[0]])
      this.sameHoursStartHour.set(resolvedSameHours.startHour)
      this.sameHoursStartMinute.set(resolvedSameHours.startMinute)
      this.sameHoursEndHour.set(resolvedSameHours.endHour)
      this.sameHoursEndMinute.set(resolvedSameHours.endMinute)
      if (mode === 'same') {
        const next = { ...map }
        for (const d of this.weekDayOptions) {
          next[d] = { ...resolvedSameHours }
        }
        this.perDayHours.set(next)
      }
    } else {
      this._applyIndefiniteFallbackFromPublicAndRows(rows ?? [], publicStartIso, eventTimezone)
    }
  }

  private _applyIndefiniteFallbackFromPublicAndRows(rows: EventTimings[], publicStartIso?: string, eventTimezone?: string | null): void {
    const openSlotRow = (rows ?? []).find((r) => r.state !== EventTimingStatus.INACTIVE && firstTimingSlot(r.slots))
    let shP: string
    let sm: string
    let ehP: string
    let em: string
    const fromSlots = perDayHoursFromFirstSlot(openSlotRow?.slots, eventTimezone)
    if (fromSlots) {
      const coerced = coerceIndefiniteWeeklyRange(fromSlots)
      shP = coerced.startHour
      sm = coerced.startMinute
      ehP = coerced.endHour
      em = coerced.endMinute
    } else {
      const { time } = parsePublicDateTimeFromApi(publicStartIso ?? '')
      const fromHm = (time || '').slice(0, 5)
      if (!fromHm) return
      const toHm = '22:00'
      const [shRaw, smRaw] = fromHm.split(':')
      const [ehRaw, emRaw] = toHm.split(':')
      shP = padHourPart(shRaw ?? '10')
      ehP = padHourPart(ehRaw ?? '22')
      sm = clampMinuteToken(smRaw ?? '0')
      em = clampMinuteToken(emRaw ?? '0')
      const coerced = coerceIndefiniteWeeklyRange({ startHour: shP, startMinute: sm, endHour: ehP, endMinute: em })
      shP = coerced.startHour
      sm = coerced.startMinute
      ehP = coerced.endHour
      em = coerced.endMinute
    }
    this.openingHoursMode.set('same')
    this.sameHoursStartHour.set(shP)
    this.sameHoursStartMinute.set(sm)
    this.sameHoursEndHour.set(ehP)
    this.sameHoursEndMinute.set(em)
    const map = { ...this.perDayHours() }
    for (const d of this.weekDayOptions) {
      map[d] = { startHour: shP, startMinute: sm, endHour: ehP, endMinute: em }
    }
    this.perDayHours.set(map)
  }

  private _applyEditModalTimeStringsToSelects(): void {
    const sh = this._parseTimeToHourMinute(this.editModalStartTime())
    const eh = this._parseTimeToHourMinute(this.editModalEndTime())
    this.editModalStartHour.set(sh.h)
    this.editModalStartMinute.set(sh.m)
    this.editModalEndHour.set(eh.h)
    this.editModalEndMinute.set(eh.m)
  }

  private _parseTimeToHourMinute(t: string): { h: string; m: string } {
    const match = /^(\d{1,2}):(\d{2})$/.exec(t?.trim() || '')
    if (!match) {
      return { h: '00', m: '00' }
    }
    const hNum = Number.parseInt(match[1], 10)
    const minNum = Number.parseInt(match[2], 10)
    if (hNum < 0 || hNum > 23 || minNum < 0 || minNum > 59) {
      return { h: '00', m: '00' }
    }
    const m = String(minNum)
    const snapped = (['00', '15', '30', '45', '59'] as const).includes(m as any) ? m : String(snapMinuteToEventPickerGrid(minNum))
    return { h: String(hNum).padStart(2, '0'), m: snapped }
  }

  private _updateDayLocal(dayId: string, updater: (day: EventDay) => EventDay): void {
    this.days.update((days) => days.map((d) => (d.id === dayId ? updater(d) : d)))
  }

  /**
   * @description True for finite, non-weekly mode with an end date so the main time range can track scheduler edge days.
   */
  private _shouldSyncEventTimeRangeFromSchedulerEdgeSlots(): boolean {
    if (this.isIndefinite()) return false
    if (this.showWeeklyHoursEditor()) return false
    const dr = this.dateRange()
    return Boolean(dr?.startDate && dr.endDate)
  }

  /**
   * @description Set main `timeRange` from the first day's earliest slot start and the last day's latest slot end.
   * @param days - Full finite day list in chronological order.
   */
  private _syncEventTimeRangeFromSchedulerEdgeSlots(days: EventDay[]): void {
    if (!this._shouldSyncEventTimeRangeFromSchedulerEdgeSlots()) return
    if (!days.length) return
    const prev = this.timeRange()
    const baseStart: string = this.normalizeHm((prev?.startTime ?? '00:00').slice(0, 5))
    const baseEnd: string = this.normalizeHm((prev?.endTime ?? '23:59').slice(0, 5))
    const { nextStart, nextEnd } = this._computeEdgeSlotMainTimes(days, baseStart, baseEnd)
    if (prev && nextStart === baseStart && nextEnd === baseEnd) return
    this.timeRange.set({ startTime: nextStart, endTime: nextEnd })
  }

  /**
   * @description Resets timing exceptions after a confirmed main date range change.
   * @param organizationId - Organization id.
   * @param eventId - Event id.
   */
  private _resetTimingExceptionsIfPending(organizationId: number, eventId: number): void {
    if (!this._exceptionsResetPending) return
    this._exceptionsResetPending = false
    this.isResettingTimingExceptions.set(true)
    this._eventService
      .resetTimingExceptions(organizationId, eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.timingExceptionsResetVersion.update((v) => v + 1)
          this.isResettingTimingExceptions.set(false)
        },
        error: (err: unknown) => {
          const msg = (err as { message?: string } | null)?.message ?? 'Failed to reset exception days'
          this._alertService.alert(Alert.ERROR, msg)
          this.isResettingTimingExceptions.set(false)
        }
      })
  }

  /**
   * @description Main opening/closing times implied by edge-day slots (earliest start on first day, latest end on last day).
   * @param dayList - Current finite day list.
   * @param baseStart - Fallback start when the first day has no slot times.
   * @param baseEnd - Fallback end when the last day has no slot times.
   */
  private _computeEdgeSlotMainTimes(dayList: EventDay[], baseStart: string, baseEnd: string): { nextStart: string; nextEnd: string } {
    if (!dayList.length) return { nextStart: baseStart, nextEnd: baseEnd }
    const firstStart = this.getEarliestSlotStartHm(dayList[0])
    const lastEnd = this.getLatestSlotEndHm(dayList[dayList.length - 1])
    return {
      nextStart: firstStart ?? baseStart,
      nextEnd: lastEnd ?? baseEnd
    }
  }

  /**
   * @description Builds event patch payload to update only public start/end datetimes
   *   using first/last day slot extrema.
   * @param _changedDayId - Day id that was edited/added (reserved for callers).
   * @returns Event patch payload, or `null` when unavailable.
   */
  private _buildPublicDatePatchForEdgeDayChange(_changedDayId: string): Pick<Event, 'public_start_date' | 'public_end_date'> | null {
    const dr = this.dateRange()
    const tr = this.timeRange()
    const dayList = this.days()
    if (!dr?.startDate || !tr || dayList.length === 0) return null
    const endCal = dr.endDate ?? dr.startDate
    const baseStart = this.normalizeHm((tr.startTime ?? '00:00').slice(0, 5))
    const baseEnd = this.normalizeHm((tr.endTime ?? '23:59').slice(0, 5))
    const { nextStart, nextEnd } = this._computeEdgeSlotMainTimes(dayList, baseStart, baseEnd)

    return {
      public_start_date: formatPublicDateTimeForTimingsPayload(dr.startDate, nextStart),
      public_end_date: formatPublicDateTimeForTimingsPayload(endCal, nextEnd)
    }
  }

  /**
   * @description Syncs visible time-range field with first/last day slot extrema after an edge-day bulk save.
   */
  private _syncTimeRangeWithEdgeDayChange(_changedDayId: string): void {
    this._syncEventTimeRangeFromSchedulerEdgeSlots(this.days())
  }

  /**
   * @description Calculates inclusive day count for a finite YMD date range (start..end).
   * @param startYmd - Start date in `YYYY-MM-DD`.
   * @param endYmd - End date in `YYYY-MM-DD`.
   * @returns Inclusive number of days (0 when invalid).
   */
  private getInclusiveDaysBetween(startYmd: string, endYmd: string): number {
    const startToken: string = (startYmd ?? '').slice(0, 10)
    const endToken: string = (endYmd ?? '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startToken) || !/^\d{4}-\d{2}-\d{2}$/.test(endToken)) return 0

    const [sYear, sMonth, sDay] = startToken.split('-').map(Number)
    const [eYear, eMonth, eDay] = endToken.split('-').map(Number)
    const startUtcMs = Date.UTC(sYear, sMonth - 1, sDay)
    const endUtcMs = Date.UTC(eYear, eMonth - 1, eDay)
    if (Number.isNaN(startUtcMs) || Number.isNaN(endUtcMs) || endUtcMs < startUtcMs) return 0

    const diff: number = endUtcMs - startUtcMs
    return Math.floor(diff / (24 * 60 * 60 * 1000)) + 1
  }

  /**
   * @description Determines whether the main time range should be forced back to default time range.
   * @param range - Selected main date range.
   * @returns `true` for indefinite ranges or finite ranges > 30 days.
   */
  private shouldForceDefaultMainTimeRange(range: { startDate: string; endDate: string | null } | null): boolean {
    if (!range?.startDate) return false
    if (!range.endDate) return true
    return this.getInclusiveDaysBetween(range.startDate, range.endDate) > 30
  }

  /**
   * @description Normalizes `24:00` values to `23:59` and returns a safe `HH:mm`.
   * @param hm - Time string token.
   * @returns Normalized time token.
   */
  private normalizeHm(hm: string): string {
    const t: string = (hm ?? '').slice(0, 5)
    if (t === '24:00' || t.startsWith('24:')) return '23:59'
    return t
  }

  /**
   * @description Returns earliest slot start (`from`) time for a given day.
   * @param day - Day row.
   * @returns Earliest `HH:mm` or `null` when unavailable.
   */
  private getEarliestSlotStartHm(day: EventDay): string | null {
    const slots = day?.slots ?? []
    if (!slots.length) return null
    let best: string | null = null
    let bestH: number = Number.POSITIVE_INFINITY
    for (const s of slots) {
      const hm: string = this.normalizeHm((s?.from ?? '').slice(0, 5))
      if (!hm) continue
      const h: number = parseTimeToHours(hm)
      if (h < bestH) {
        bestH = h
        best = hm
      }
    }
    return best
  }

  /**
   * @description Returns latest slot end (`to`) time for a given day.
   * @param day - Day row.
   * @returns Latest `HH:mm` or `null` when unavailable.
   */
  private getLatestSlotEndHm(day: EventDay): string | null {
    const slots = day?.slots ?? []
    if (!slots.length) return null
    let best: string | null = null
    let bestH: number = Number.NEGATIVE_INFINITY
    for (const s of slots) {
      const hm: string = this.normalizeHm((s?.to ?? '').slice(0, 5))
      if (!hm) continue
      const h: number = parseTimeToHours(hm)
      if (h > bestH) {
        bestH = h
        best = hm
      }
    }
    return best
  }

  /**
   * @description Computes public start/end override from first-day earliest and last-day latest slot times.
   * @param opts - Date range, selected time range and generated days.
   * @returns Override times, or `null` when they match the current main range.
   */
  private getPublicTimeOverrideForFiniteEdgeSlots(opts: {
    dateRange: { startDate: string; endDate: string | null }
    timeRange: { startTime: string; endTime: string }
    days: EventDay[]
  }): { publicStartTime: string; publicEndTime: string } | null {
    const { dateRange, timeRange, days } = opts
    if (!dateRange.startDate || !dateRange.endDate) return null
    if (!days.length) return null
    const baseStart: string = this.normalizeHm((timeRange.startTime ?? '').slice(0, 5))
    const baseEnd: string = this.normalizeHm((timeRange.endTime ?? '23:59').slice(0, 5))
    const { nextStart, nextEnd } = this._computeEdgeSlotMainTimes(days, baseStart, baseEnd)
    if (nextStart === baseStart && nextEnd === baseEnd) return null
    return { publicStartTime: nextStart, publicEndTime: nextEnd }
  }

  /**
   * @description Apply same-hours patch then persist.
   * @param patch Partial<{ startHour: string; startMinute: string; endHour: string; endMinute: string }>
   */
  public onSameHoursPatch(patch: Partial<{ startHour: string; startMinute: string; endHour: string; endMinute: string }>): void {
    if (patch.startHour != null) this.sameHoursStartHour.set(patch.startHour)
    if (patch.startMinute != null) this.sameHoursStartMinute.set(patch.startMinute)
    if (patch.endHour != null) this.sameHoursEndHour.set(patch.endHour)
    if (patch.endMinute != null) this.sameHoursEndMinute.set(patch.endMinute)
    this.onSameHoursChange()
  }
}
